import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { makeGetTask, makeGetTaskActiveSubtasks, makeGetTaskCompletedSubtasks } from '../selectors/tasks';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { fetchTasks, createTask, updateTask, moveTask, removeTask, addTaskLabels } from '../actions/tasks';

import { getDndContext } from '../services/dnd-service';
import { trackEvent } from '../services/events-tracker-service';
import { getCurrentUserId } from '../services/store-service';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';

import CanUser from './can-user';
import SubtaskHolder from './subtask-holder';

import subtaskModel from '../models/subtask.model';
import SubtaskPlaceholder from './placeholders/subtask-item.placeholder';

import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowUp from 'icons/icon-arrow-up';

class Subtasks extends PureComponent {
    static propTypes = {
        projectId: PropTypes.string,
        taskId: PropTypes.string,
        loading: PropTypes.bool,
        activeSubtasks: PropTypes.arrayOf(PropTypes.string),
        completedSubtasks: PropTypes.arrayOf(PropTypes.string),
        provideController: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.state = {
            showCreateNew: true,
            isDragging: false,
            subtasksInEditMode: [],
            subtasksCollapsed: getUserPreference('task_subtasks_collapsed') || false
        };

        this.newSubtask = subtaskModel.create();
        this.newSubtaskCtrl = null;
        this.addNewAfterSave = false;
    }

    handleSubtaskComplete = subtaskId => {
        this.changeCompletedState(subtaskId, true);
    }

    handleSubtaskUncomplete = subtaskId => {
        this.changeCompletedState(subtaskId, false);
    }

    handleSubtaskNameChange = (subtaskId, name) => {
        let promise = null;
        if (!subtaskId) {
            this.newSubtask.name = name;
            promise = this.createSubtask(this.newSubtask);
        } else {
            promise = new Promise(resolve => {
                this.updateSubtask(subtaskId, { name });

                resolve();
            });
        }

        promise.then(() => this.addSubtask());
    }

    moveSubtask = (dragId, hoverId, preventSave) => {
        const draggedItems = [...(this.state.draggedItems || [])];
        const hoverIndex = draggedItems.indexOf(hoverId);

        draggedItems.splice(draggedItems.indexOf(dragId), 1);
        draggedItems.splice(hoverIndex, 0, dragId);

        if (preventSave) {
            this.setState({ draggedItems })
        } else {
            draggedItems.forEach((subtaskId, order) => {
                if (subtaskId === dragId) {
                    moveTask(subtaskId, {
                        type: 'task',
                        id: this.props.taskId,
                        order: order === draggedItems.length - 1 ? order + 1 : order
                    });
                }

                updateTask(subtaskId, { order }, true);
            });
        }
    }

    handleDragStart = () => {
        this.setState({
            draggedItems: [...this.props.activeSubtasks],
            isDragging: true
        });
    }

    handleDragEnd = () => {
        this.setState({
            draggedItems: null,
            isDragging: false
        });
    }

    handleToggleSubtasksClick = () => {
        setUserPreference('task_subtasks_collapsed', !this.state.subtasksCollapsed);

        this.setState({
            subtasksCollapsed: !this.state.subtasksCollapsed
        });
    }

    handleSubtaskEnterPress = () => {
        this.addNewAfterSave = true;
    }

    handleSubtaskDelete = subtaskId => {
        removeTask(subtaskId);

        trackEvent('SUBTASK.REMOVE');
    }

    handleConvertToTask = (subtaskId, taskGroupId, name) => {
        trackEvent('TASK.CREATE', {
            via: `${this.props.page} - from subtask`
        });

        return updateTask(subtaskId, {
            task_group: taskGroupId,
            task: null
        }).then(() => {
            if (this.props.task && this.props.task.labels) {
                return addTaskLabels(subtaskId, this.props.task.labels);
            }
        });
    }

    handleMoveToTask = (subtaskId, taskId) => {
        updateTask(subtaskId, {
            task: taskId
        });
    }

    handleSubtaskEditStart = subtaskId => {
        this.setState({
            subtasksInEditMode: [...new Set([...this.state.subtasksInEditMode, subtaskId])] 
        });
    }

    handleSubtaskEditEnd = subtaskId => {
        this.setState({
            subtasksInEditMode: this.state.subtasksInEditMode.filter(sId => sId !== subtaskId)
        });
    }

    changeCompletedState(subtaskId, completed) {
        const subtaskProps = {
            completed
        };

        let completedSubtasksCount = this.props.completedSubtasks.length;

        if (completed === true) {
            completedSubtasksCount += 1;
            subtaskProps.completed_by = getCurrentUserId();
            subtaskProps.completed_on = new Date();

            trackEvent('SUBTASK.COMPLETE');
        } else {
            completedSubtasksCount -= 1;
            trackEvent('SUBTASK.ACTIVATE');
        }

        this.updateTask({
            completed_subtasks: completedSubtasksCount
        }, true);

        this.updateSubtask(subtaskId, subtaskProps);
    }

    updateSubtask(subtaskId, subtaskProps, preventSave = false) {
        updateTask(subtaskId, subtaskProps, preventSave);
    }

    updateTask(taskProps, preventSave = false) {
        updateTask(this.props.taskId, taskProps, preventSave);
    }

    addSubtask() {
        if (this.state.showCreateNew && this.newSubtaskCtrl) {
            this.newSubtaskCtrl.beginEdit();

            return;
        }

        this.newSubtask = subtaskModel.create();

        this.setState({
            showCreateNew: true
        }, () => {
            this.newSubtaskCtrl.beginEdit();
        });
    }

    createSubtask(nextSubtask) {
        return new Promise(resolve => {
            createTask({
                task: this.props.taskId,
                name: nextSubtask.name,
                // order: this.props.activeSubtasks.length,
                completed: nextSubtask.completed
            }).then(() => {
                trackEvent('SUBTASK.CREATE');
            });

            this.setState({
                showCreateNew: false
            }, resolve);
        });
    }

    focusSubtask(subtask) {
        subtask && subtask.focus();
    }

    init() {}

    get subtasks() {
        return this.props.subtasks;
    }

    async fetchSubtasks() {
        await fetchTasks({
            taskId: this.props.taskId
        }, true);
    }

    attemptFetch() {
        if (this.props.taskId && !this.props.fetched && !this.props.loading) {
            this.fetchSubtasks();
        }
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            addSubtask: () => {
                this.addSubtask();
            }
        });

        if (this.props.fetched && !this.props.loading) {
            this.init();
        }

        this.attemptFetch();
    }

    componentDidUpdate(prevProps) {
        if (this.props.fethed && !this.props.loading && (prevProps.loading || !prevProps.fetched)) {
            this.init();
        }

        this.attemptFetch();
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    render() {
        const activeSubtasks = this.state.draggedItems || this.props.activeSubtasks;
        const completedSubtasks = this.props.completedSubtasks;

        if (this.props.loading) {
            return (
                <div className="subtasks-holder">
                    <div className="subtasks has-items">
                        <SubtaskPlaceholder key={1} />

                        <SubtaskPlaceholder key={2} />

                        <SubtaskPlaceholder key={3} />
                    </div>
                </div>
            )
        }

        return (
            <div className={'subtasks-holder' + (!this.state.subtasksCollapsed ? ' show-completed' : '')}>
                <div className={'subtasks has-items' + (this.state.isDragging ? ' is-dragging' : '')}>
                    {
                        activeSubtasks.map((subtaskId, index) =>
                            <SubtaskHolder
                                key={subtaskId}
                                richPreview={true}
                                id={subtaskId}
                                index={index}
                                projectId={this.props.projectId}
                                subtasksInEditMode={this.state.subtasksInEditMode}
                                onComplete={this.handleSubtaskComplete}
                                onUncomplete={this.handleSubtaskUncomplete}
                                onNameChange={this.handleSubtaskNameChange}
                                onEnterPress={this.handleSubtaskEnterPress}
                                onDelete={this.handleSubtaskDelete}
                                onConvertToTask={this.handleConvertToTask}
                                onMoveToTask={this.handleMoveToTask}
                                moveSubtask={this.moveSubtask}
                                onDragStart={this.handleDragStart}
                                onDragEnd={this.handleDragEnd}
                                onEditStart={this.handleSubtaskEditStart}
                                onEditEnd={this.handleSubtaskEditEnd}
                            />
                        )
                    }

                    {
                        this.state.showCreateNew ? (
                            <CanUser type="createSubtask">
                                <SubtaskHolder
                                    key="new"
                                    new={true}
                                    onEnterPress={this.handleSubtaskEnterPress}
                                    onNameChange={this.handleSubtaskNameChange}
                                    provideController={ctrl => {this.newSubtaskCtrl = ctrl}}
                                />
                            </CanUser>
                        ) : null
                    }
                </div>

                {
                    completedSubtasks.length ? (
                        <div className="subtasks-list-toggle">
                            <span onClick={this.handleToggleSubtasksClick}>
                                {completedSubtasks.length} Completed Subtasks

                                {
                                    this.state.subtasksCollapsed ? <IconArrowDown /> : <IconArrowUp />
                                }
                            </span>
                        </div>
                    ) : null
                }

                <div className={'subtasks subtasks-completed' + (completedSubtasks.length ? ' has-items' : '')}>
                    {
                        completedSubtasks.map((subtaskId, index) =>
                            <SubtaskHolder
                                key={subtaskId}
                                richPreview={true}
                                id={subtaskId}
                                index={index}
                                projectId={this.props.projectId}
                                onComplete={this.handleSubtaskComplete}
                                onUncomplete={this.handleSubtaskUncomplete}
                                onDelete={this.handleSubtaskDelete}
                            />
                        )
                    }
                </div>
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getTaskActiveSubtasks = makeGetTaskActiveSubtasks();
    const getTaskCompletedSubtasks = makeGetTaskCompletedSubtasks();
    const getTasksFetched = makeGetFetchedState();
    const getTasksLoading = makeGetLoadingState();

    return ({ tasks }, ownProps) => ({
        task: getTask(tasks.items, ownProps.taskId),
        activeSubtasks: getTaskActiveSubtasks(tasks.items, ownProps.taskId).map(task => task.id),
        completedSubtasks: getTaskCompletedSubtasks(tasks.items, ownProps.taskId).map(task => task.id),
        fetched: getTasksFetched(tasks.fetchInfo, 'task', ownProps.taskId),
        loading: getTasksLoading(tasks.fetchInfo, 'task', ownProps.taskId)
    });
};


export default connect(makeMapStateToProps)(getDndContext()(Subtasks));
